import { DEFAULT_SETTINGS } from '../constants'
import type { AddonConfig, AddonResource, Settings, StremioStream, Subtitle } from '../types'
import { getStreams, getSubtitles, normalizeUrl } from './stremio'

function hashText(value: string) {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) hash = Math.imul(hash ^ value.charCodeAt(index), 0x01000193)
  return (hash >>> 0).toString(36)
}

export function addonId(url: string) {
  return `addon-${hashText(normalizeUrl(url).toLowerCase())}`
}

function cleanAddon(value: AddonConfig, index: number): AddonConfig | null {
  try {
    if (typeof value?.url !== 'string' || value.url.length > 500) return null
    const url = normalizeUrl(value.url)
    const parsed = new URL(url)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    const resources = Array.isArray(value.resources)
      ? value.resources.filter((resource): resource is AddonResource => ['catalog', 'meta', 'stream', 'subtitles'].includes(resource))
      : undefined
    return {
      id: value.id?.slice(0, 80) || addonId(url),
      name: value.name?.trim().slice(0, 80) || parsed.hostname,
      url,
      enabled: value.enabled !== false,
      priority: Number.isFinite(value.priority) ? Math.max(0, Math.floor(value.priority)) : index,
      resources: resources?.length ? Array.from(new Set(resources)) : undefined,
    }
  } catch { return null }
}

function supports(addon: AddonConfig, resource: AddonResource) {
  return !addon.resources || addon.resources.includes(resource)
}

function validAlias(value: string | undefined, fallback: string) {
  try {
    if (typeof value !== 'string' || value.length > 500) return fallback
    const url = normalizeUrl(value || '')
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? url : fallback
  } catch { return fallback }
}

function endpointName(url: string, fallback: string) {
  try { return new URL(url).hostname.slice(0, 80) || fallback }
  catch { return fallback }
}

function booleanSetting(value: unknown, fallback: boolean) {
  return typeof value === 'boolean' ? value : fallback
}

function enumSetting<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && allowed.includes(value as T) ? value as T : fallback
}

function languageSetting(value: unknown, fallback: string) {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,12}$/.test(value.trim())
    ? value.trim() : fallback
}

function dedupeAddons(addons: AddonConfig[]) {
  const seen = new Set<string>()
  return addons.filter((addon) => {
    const key = addon.url.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function sameResources(left: AddonResource[] | undefined, right: AddonResource[] | undefined) {
  if (left === right) return true
  if (!left || !right || left.length !== right.length) return false
  return left.every((resource, index) => resource === right[index])
}

const ADDON_KEYS = ['id', 'name', 'url', 'enabled', 'priority', 'resources']

/**
 * Normalizing an unchanged list must not hand back a new array. Effects and
 * memos all over the app key on `settings.addons`, so a fresh identity for an
 * unrelated save (subtitle size, HDR mode) used to re-run every add-on request
 * — including one that dropped the subtitle track mid-playback.
 *
 * Identity is only reused when the stored entry is already exactly what
 * cleaning would produce, key set included. A hand-edited entry carrying a
 * stray field still gets the sanitized copy: the paired-phone contract rejects
 * a whole state frame over one unexpected add-on key.
 */
function preservedAddons(normalized: AddonConfig[], original: unknown): AddonConfig[] {
  if (!Array.isArray(original) || original.length !== normalized.length) return normalized
  const unchanged = normalized.every((addon, index) => {
    const previous = original[index] as AddonConfig | undefined
    if (!previous || typeof previous !== 'object') return false
    if (Object.keys(previous).some((key) => !ADDON_KEYS.includes(key))) return false
    return addon.id === previous.id && addon.name === previous.name
      && addon.url === previous.url && addon.enabled === previous.enabled
      && addon.priority === previous.priority && sameResources(addon.resources, previous.resources)
  })
  return unchanged ? original as AddonConfig[] : normalized
}

/** Migrate the original single stream/subtitle URL fields without breaking
 * saved installations. The legacy fields remain the primary-provider aliases
 * used by the pairing contract. */
export function normalizeSettings(value: Settings): Settings {
  const raw = Array.isArray(value.addons) ? value.addons : []
  let addons = dedupeAddons(raw.map(cleanAddon).filter((addon): addon is AddonConfig => Boolean(addon)))
  const torrentioUrl = validAlias(value.torrentioUrl, DEFAULT_SETTINGS.torrentioUrl)
  const subtitlesUrl = validAlias(value.subtitlesUrl, DEFAULT_SETTINGS.subtitlesUrl)

  // Only legacy settings without an ordered list are seeded from the old URL
  // aliases. Once migrated, membership is authoritative: deleting every
  // provider must survive save/reload.
  if (value.addonsConfigured !== true && value.torrentioEnabled && !addons.some((addon) => addon.url === torrentioUrl)) {
    addons.push({
      id: addonId(torrentioUrl), name: 'Primary source add-on', url: torrentioUrl,
      enabled: value.torrentioEnabled, priority: addons.length, resources: ['catalog', 'meta', 'stream'],
    })
  }
  if (value.addonsConfigured !== true && !addons.some((addon) => addon.url === subtitlesUrl)) {
    addons.push({
      id: addonId(subtitlesUrl), name: 'Subtitle add-on', url: subtitlesUrl,
      enabled: true, priority: addons.length, resources: ['subtitles'],
    })
  }
  addons = preservedAddons(
    addons
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 12)
      .map((addon, priority) => ({ ...addon, priority })),
    value.addons,
  )
  const primaryStream = addons.find((addon) => addon.enabled && supports(addon, 'stream'))
    || addons.find((addon) => supports(addon, 'stream'))
  const primarySubtitle = addons.find((addon) => addon.enabled && supports(addon, 'subtitles'))
    || addons.find((addon) => supports(addon, 'subtitles'))
  return {
    serverUrl: validAlias(value.serverUrl, DEFAULT_SETTINGS.serverUrl),
    torrentioUrl: primaryStream?.url || torrentioUrl,
    torrentioEnabled: addons.some((addon) => addon.enabled && supports(addon, 'stream')),
    autoplayNext: booleanSetting(value.autoplayNext, DEFAULT_SETTINGS.autoplayNext),
    preferHls: booleanSetting(value.preferHls, DEFAULT_SETTINGS.preferHls),
    subtitlesEnabled: booleanSetting(value.subtitlesEnabled, DEFAULT_SETTINGS.subtitlesEnabled),
    subtitleLanguage: languageSetting(value.subtitleLanguage, DEFAULT_SETTINGS.subtitleLanguage),
    subtitleSize: enumSetting(value.subtitleSize, ['small', 'medium', 'large'] as const, DEFAULT_SETTINGS.subtitleSize),
    subtitlesUrl: primarySubtitle?.url || subtitlesUrl,
    hdrMode: enumSetting(value.hdrMode, ['auto', 'prefer', 'sdr'] as const, DEFAULT_SETTINGS.hdrMode),
    addonsConfigured: true,
    addons,
    subtitleDelay: Number.isFinite(value.subtitleDelay) ? Math.max(-30, Math.min(30, value.subtitleDelay)) : DEFAULT_SETTINGS.subtitleDelay,
    subtitlePosition: enumSetting(value.subtitlePosition, ['bottom', 'middle', 'top'] as const, DEFAULT_SETTINGS.subtitlePosition),
    subtitleBackground: enumSetting(value.subtitleBackground, ['none', 'shadow', 'box'] as const, DEFAULT_SETTINGS.subtitleBackground),
    subtitleSdh: enumSetting(value.subtitleSdh, ['include', 'prefer', 'exclude'] as const, DEFAULT_SETTINGS.subtitleSdh),
    subtitleForced: enumSetting(value.subtitleForced, ['include', 'prefer', 'only'] as const, DEFAULT_SETTINGS.subtitleForced),
    subtitleEncoding: enumSetting(value.subtitleEncoding, ['auto', 'utf-8', 'windows-1252'] as const, DEFAULT_SETTINGS.subtitleEncoding),
    preferredAudioLanguage: languageSetting(value.preferredAudioLanguage, DEFAULT_SETTINGS.preferredAudioLanguage),
    restorePlayback: booleanSetting(value.restorePlayback, DEFAULT_SETTINGS.restorePlayback),
    oledProtection: booleanSetting(value.oledProtection, DEFAULT_SETTINGS.oledProtection),
  }
}

/**
 * Priority order is an invariant of the boundaries, not of this call: stored
 * settings are normalized by `getSettings`/`saveSettings`, and settings arriving
 * from a paired phone are rejected unless every `priority` equals its index. So
 * this only has to filter — it used to re-normalize the whole object on every
 * call, including once per progress tick during playback.
 */
export function enabledAddons(settings: Settings, resource: AddonResource) {
  const addons = Array.isArray(settings.addons) ? settings.addons : []
  return addons.filter((addon) => addon.enabled && supports(addon, resource))
}

export function withAddons(settings: Settings, addons: AddonConfig[]) {
  const ordered = addons.map((addon, priority) => ({ ...addon, priority }))
  const primaryStream = ordered.find((addon) => addon.enabled && supports(addon, 'stream'))
    || ordered.find((addon) => supports(addon, 'stream'))
  const primarySubtitle = ordered.find((addon) => addon.enabled && supports(addon, 'subtitles'))
    || ordered.find((addon) => supports(addon, 'subtitles'))
  return normalizeSettings({
    ...settings,
    addonsConfigured: true,
    addons: ordered,
    torrentioUrl: primaryStream?.url || settings.torrentioUrl,
    torrentioEnabled: ordered.some((addon) => addon.enabled && supports(addon, 'stream')),
    subtitlesUrl: primarySubtitle?.url || settings.subtitlesUrl,
  })
}

/** Keep the original single-provider controls and phone contract useful while
 * treating the ordered add-on list as the source of truth. This is called only
 * when an old alias changed without an explicit add-on-list edit. */
export function reconcileLegacySettings(current: Settings, desired: Settings) {
  let addons = normalizeSettings(current).addons.map((addon) => ({ ...addon }))
  const streamUrlChanged = normalizeUrl(desired.torrentioUrl) !== normalizeUrl(current.torrentioUrl)
  const streamEnabledChanged = desired.torrentioEnabled !== current.torrentioEnabled
  const subtitleUrlChanged = normalizeUrl(desired.subtitlesUrl) !== normalizeUrl(current.subtitlesUrl)

  if (streamUrlChanged) {
    const url = validAlias(desired.torrentioUrl, current.torrentioUrl)
    const exact = addons.findIndex((addon) => addon.url === url && supports(addon, 'stream'))
    const primary = addons.findIndex((addon) => supports(addon, 'stream'))
    if (exact >= 0) {
      const [match] = addons.splice(exact, 1)
      addons.unshift({ ...match, enabled: desired.torrentioEnabled })
    } else if (primary >= 0) {
      addons[primary] = {
        ...addons[primary], id: addonId(url), name: endpointName(url, 'Primary source add-on'),
        url, enabled: desired.torrentioEnabled,
      }
    } else if (desired.torrentioEnabled) {
      addons.unshift({
        id: addonId(url), name: 'Primary source add-on', url, enabled: true, priority: 0,
        resources: ['catalog', 'meta', 'stream'],
      })
    }
  }

  if (streamEnabledChanged) {
    if (!desired.torrentioEnabled) {
      addons = addons.map((addon) => supports(addon, 'stream') ? { ...addon, enabled: false } : addon)
    } else if (!addons.some((addon) => addon.enabled && supports(addon, 'stream'))) {
      const primary = addons.findIndex((addon) => supports(addon, 'stream'))
      if (primary >= 0) addons[primary] = { ...addons[primary], enabled: true }
      else {
        const url = validAlias(desired.torrentioUrl, current.torrentioUrl)
        addons.unshift({
          id: addonId(url), name: 'Primary source add-on', url, enabled: true, priority: 0,
          resources: ['catalog', 'meta', 'stream'],
        })
      }
    }
  }

  if (subtitleUrlChanged) {
    const url = validAlias(desired.subtitlesUrl, current.subtitlesUrl)
    const exact = addons.findIndex((addon) => addon.url === url && supports(addon, 'subtitles'))
    const primary = addons.findIndex((addon) => supports(addon, 'subtitles'))
    if (exact >= 0) {
      if (primary >= 0 && primary !== exact) [addons[primary], addons[exact]] = [addons[exact], addons[primary]]
    } else if (primary >= 0) {
      addons[primary] = {
        ...addons[primary], id: addonId(url), name: endpointName(url, 'Subtitle add-on'), url,
      }
    } else {
      addons.push({ id: addonId(url), name: 'Subtitle add-on', url, enabled: true, priority: addons.length, resources: ['subtitles'] })
    }
  }

  return withAddons({ ...desired, addonsConfigured: true }, addons)
}

function dedupeStreams(streams: StremioStream[]) {
  const seen = new Set<string>()
  return streams.filter((stream) => {
    const key = stream.infoHash
      ? `torrent:${stream.infoHash.toLowerCase()}:${stream.fileIdx ?? (stream.fileMustInclude || []).join(',')}`
      : `url:${(stream.url || '').replace(/[?#].*$/, '')}:${stream.behaviorHints?.filename || stream.title || stream.name || ''}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export async function getStreamsFromAddons(settings: Settings, type: 'movie' | 'series', id: string) {
  const providers = enabledAddons(settings, 'stream')
  const results = await Promise.allSettled(providers.map(async (addon) => (
    (await getStreams(addon.url, type, id)).map((stream) => ({ ...stream, addonUrl: addon.url }))
  )))
  if (!results.some((result) => result.status === 'fulfilled')) {
    const failed = results.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined
    throw failed?.reason instanceof Error ? failed.reason : new Error('No enabled stream add-on is reachable.')
  }
  return dedupeStreams(results.flatMap((result) => result.status === 'fulfilled' ? result.value : []))
}

export async function getSubtitlesFromAddons(settings: Settings, type: 'movie' | 'series', id: string) {
  const providers = enabledAddons(settings, 'subtitles')
  if (!providers.length) throw new Error('No subtitle add-on is enabled.')
  const results = await Promise.allSettled(providers.map(async (addon) => (
    (await getSubtitles(addon.url, type, id)).map((subtitle) => ({ ...subtitle, addonName: addon.name }))
  )))
  if (!results.some((result) => result.status === 'fulfilled')) {
    throw new Error(providers.length === 1
      ? `${providers[0].name} could not be reached.`
      : 'No enabled subtitle add-on could be reached.')
  }
  const seen = new Set<string>()
  return results.flatMap((result) => result.status === 'fulfilled' ? result.value : []).filter((subtitle: Subtitle & { addonName?: string }) => {
    const key = `${subtitle.lang}:${subtitle.url}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
